import React, { createContext, useContext, useState, forwardRef } from 'react';

const TabsContext = createContext({
    value: undefined,
    onValueChange: () => { },
});

const listStyle = {
    display: 'inline-flex',
    height: '2.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--surface-sunken)',
    border: '1px solid var(--border)',
    padding: '0.25rem',
    color: 'var(--text-muted)',
    boxSizing: 'border-box'
};

const triggerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: 'var(--radius-sm)',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    fontFamily: 'var(--font-ui)',
    lineHeight: '1.25rem',
    transition: 'all 0.15s ease',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: 'var(--text-muted)'
};

const activeTriggerStyle = {
    backgroundColor: 'var(--surface-raised)',
    boxShadow: 'var(--shadow-sm)',
    color: 'var(--text)',
    fontWeight: '600'
};

export const Tabs = forwardRef(({ defaultValue, value, onValueChange, className, children, style, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue);

    const handleValueChange = (newValue) => {
        if (value !== undefined) {
            onValueChange?.(newValue);
        } else {
            setInternalValue(newValue);
        }
    };

    const activeValue = value !== undefined ? value : internalValue;

    return (
        <TabsContext.Provider value={{ value: activeValue, onValueChange: handleValueChange }}>
            <div
                ref={ref}
                className={className}
                style={{ ...style }}
                {...props}
            >
                {children}
            </div>
        </TabsContext.Provider>
    );
});
Tabs.displayName = 'Tabs';

export const TabsList = forwardRef(({ className, children, style, ...props }, ref) => {
    return (
        <div
            ref={ref}
            className={className}
            style={{ ...listStyle, ...style }}
            {...props}
        >
            {children}
        </div>
    );
});
TabsList.displayName = 'TabsList';

export const TabsTrigger = forwardRef(({ className, value, children, style, ...props }, ref) => {
    const { value: selectedValue, onValueChange } = useContext(TabsContext);
    const isActive = selectedValue === value;

    return (
        <button
            ref={ref}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onValueChange(value)}
            className={className}
            style={{
                ...triggerStyle,
                ...(isActive ? activeTriggerStyle : {}),
                ...style
            }}
            {...props}
        >
            {children}
        </button>
    );
});
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef(({ className, value, children, style, ...props }, ref) => {
    const { value: selectedValue } = useContext(TabsContext);

    if (selectedValue !== value) return null;

    return (
        <div
            ref={ref}
            role="tabpanel"
            className={className}
            style={{
                marginTop: '0.75rem',
                outline: 'none',
                ...style
            }}
            {...props}
        >
            {children}
        </div>
    );
});
TabsContent.displayName = 'TabsContent';

export default Tabs;
